import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { AccessibilityInfo, FlatList, Platform, StyleSheet, View } from 'react-native';
import Animated, { FadeOut } from 'react-native-reanimated';

import { createMqStyles, iosVerticalScrollContentContainerStyle } from '@fhs/ui';
import { IRestaurantNode } from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import {
  focusStoreLocatorFirstRestaurantResult,
  hasPendingStoreLocatorFirstRestaurantFocus,
  setStoreLocatorFirstRestaurantFocusTarget,
  subscribeStoreLocatorFirstRestaurantFocusRequest,
} from '@fhs-legacy/frontend/src/pages/store-locator/store-locator-focus-handoff';
import { OrderLocatorTab } from '@fhs-legacy/frontend/src/utils/launchdarkly/variations';
import { STORE_LOCATOR_STORE_LIST_ITEM_AT } from '@fhs-legacy/frontend/src/utils/test-ids';

import { StoreLocatorEmptyStateV2 } from '../empty-state';
import { StoreCardV2 } from '../store-card';

import { StoreListSkeleton } from './store-list.skeleton';

const isWeb = Platform.OS === 'web';

const skeletonExitWebStyle = { flex: 1 } as const;

// Built once so the exiting preset isn't recreated on every render.
const skeletonExiting = FadeOut;

/**
 * FadeOut only on native, and only while this view is removed from a parent that stays mounted.
 * Absolute fill lets the placeholder dissolve over the list that is taking its place.
 */
const SkeletonExit = Platform.select<React.FC<{ children: React.ReactNode }>>({
  native: ({ children }) => (
    <Animated.View
      collapsable={false}
      exiting={skeletonExiting}
      pointerEvents="none"
      style={StyleSheet.absoluteFill}
    >
      {children}
    </Animated.View>
  ),
  default: ({ children }) => <View style={skeletonExitWebStyle}>{children}</View>,
})!;

interface StoreListProps {
  restaurants: IRestaurantNode[];
  tab: OrderLocatorTab;
  isLoading: boolean;
  hasError: boolean;
  selectedRestaurantId: string | null;
  onSelectRestaurant: (restaurantId: string) => void;
  /** Web only: matches the tab bar's `aria-controls`. */
  panelId: string;
}

export const StoreList: React.VFC<StoreListProps> = ({
  restaurants,
  tab,
  isLoading,
  hasError,
  selectedRestaurantId,
  onSelectRestaurant,
  panelId,
}) => {
  const { formatMessage } = useIntl();
  const styles = useStoreListMqStyles();
  const listRef = useRef<FlatList<IRestaurantNode>>(null);
  const [selectionAnnouncement, setSelectionAnnouncement] = useState('');
  const [focusRequestVersion, setFocusRequestVersion] = useState(0);

  const setFocusTargetRef = useCallback((node: HTMLButtonElement | null) => {
    setStoreLocatorFirstRestaurantFocusTarget(node);
  }, []);

  // Address search hands focus to the first result; keep the same target the legacy list used.
  const focusTargetIndex = useMemo(() => {
    const firstAvailable = restaurants.findIndex(restaurant => restaurant.isAvailable !== false);
    return firstAvailable >= 0 ? firstAvailable : 0;
  }, [restaurants]);

  useEffect(
    () =>
      subscribeStoreLocatorFirstRestaurantFocusRequest(() => {
        setFocusRequestVersion(version => version + 1);
      }),
    []
  );

  useEffect(() => {
    if (
      !isWeb ||
      isLoading ||
      !restaurants.length ||
      !hasPendingStoreLocatorFirstRestaurantFocus()
    ) {
      return;
    }

    let frameId: number | null = null;
    let attempts = 0;

    const focusFirstStoreResult = () => {
      attempts += 1;
      if (focusStoreLocatorFirstRestaurantResult()) {
        return;
      }
      if (attempts < 8) {
        frameId = window.requestAnimationFrame(focusFirstStoreResult);
      }
    };

    const timeoutId = window.setTimeout(focusFirstStoreResult, 0);

    return () => {
      window.clearTimeout(timeoutId);
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [focusRequestVersion, isLoading, restaurants.length]);

  const announce = useCallback((message: string) => {
    if (!message) {
      return;
    }

    if (!isWeb) {
      AccessibilityInfo.announceForAccessibility(message);
      return;
    }

    setSelectionAnnouncement('');
    requestAnimationFrame(() => setSelectionAnnouncement(message));
  }, []);

  const handleSelect = useCallback(
    (restaurant: IRestaurantNode) => {
      if (!restaurant._id) {
        return;
      }
      onSelectRestaurant(restaurant._id);

      const storeName = restaurant.name?.trim();
      if (storeName) {
        announce(`Selected store: ${storeName}.`);
      }
    },
    [announce, onSelectRestaurant]
  );

  // A pin tap selects a store that may be scrolled out of view, so follow the selection.
  useEffect(() => {
    if (!selectedRestaurantId) {
      return;
    }

    const index = restaurants.findIndex(restaurant => restaurant._id === selectedRestaurantId);
    if (index < 0) {
      return;
    }

    listRef.current?.scrollToIndex({ index, viewPosition: 0.5, animated: true });
  }, [selectedRestaurantId, restaurants]);

  const panelProps = isWeb
    ? ({ nativeID: panelId, role: 'tabpanel' } as Record<string, unknown>)
    : {};

  const skeletonLabel =
    tab === OrderLocatorTab.NEARBY
      ? formatMessage({ id: 'loadingNearbyLocations' })
      : 'Loading locations. Please wait.';

  return (
    <>
      {isWeb && !isLoading && restaurants.length ? (
        <View
          style={announcementStyles.visuallyHidden}
          {...({
            'aria-live': 'polite',
            'aria-atomic': true,
            role: 'status',
          } as Record<string, unknown>)}
        >
          {selectionAnnouncement}
        </View>
      ) : null}
      {/* Stays mounted across the skeleton → list swap so the skeleton's FadeOut can finish. */}
      <View collapsable={false} style={styles.panel} {...panelProps}>
        {isLoading ? (
          <SkeletonExit>
            <StoreListSkeleton ariaLabel={skeletonLabel} />
          </SkeletonExit>
        ) : (
          <FlatList
            ref={listRef}
            style={styles.fill}
            data={restaurants}
            keyExtractor={(item, index) => item?._id ?? `${index}_restaurant`}
            removeClippedSubviews={false}
            contentContainerStyle={[styles.listContent, iosVerticalScrollContentContainerStyle]}
            ListEmptyComponent={<StoreLocatorEmptyStateV2 tab={tab} currentTabError={hasError} />}
            // scrollToIndex on a list of variable-height cards can miss before layout settles.
            onScrollToIndexFailed={({ index }) => {
              requestAnimationFrame(() => {
                listRef.current?.scrollToIndex({ index, viewPosition: 0.5, animated: false });
              });
            }}
            renderItem={({ item, index }) => (
              <StoreCardV2
                testID={STORE_LOCATOR_STORE_LIST_ITEM_AT(index)}
                restaurant={item}
                isSelected={Boolean(item._id) && item._id === selectedRestaurantId}
                onSelect={() => handleSelect(item)}
                isStoreLocatorFocusTarget={index === focusTargetIndex}
                storeLocatorFocusRef={index === focusTargetIndex ? setFocusTargetRef : undefined}
              />
            )}
          />
        )}
      </View>
    </>
  );
};

const useStoreListMqStyles = createMqStyles({
  panel: {
    $base: {
      flex: 1,
    },
  },
  fill: {
    $base: {
      flex: 1,
    },
  },
  // Cards are full-bleed rows that own their own padding and bottom divider.
  listContent: {
    $base: {
      paddingBottom: 24,
    },
  },
});

const announcementStyles = StyleSheet.create({
  visuallyHidden: {
    borderWidth: 0,
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    width: 1,
  },
});
